<template>
  <div class="datalog-table-root">
    <!-- show/hide column modal -->
    <b-modal :id="modalId" title="Show/Hide Datalog Column" size="xl">
      <div class="d-flex justify-content-end mb-3 border-bottom pb-3">
        <button class="btn btn-sm btn-mantraz mr-2" @click="showAllColumns">Show All</button>
        <button class="btn btn-sm btn-outline-mantraz" @click="hideAllColumns">Hide All</button>
      </div>

      <div class="row">
        <div v-for="(categoryColumns, categoryKey) in columnsForModal" :key="categoryKey"
          class="col-md-4 col-lg-3 mb-4">
          <div class="d-flex justify-content-between align-items-center border-bottom pb-1 mb-2">
            <h5 class="text-capitalize font-weight-bold mb-0">{{ categoryLabel(categoryKey) }}</h5>
            <div class="form-check">
              <input type="checkbox" class="form-check-input" :id="`toggle-${categoryKey}`"
                :checked="isCategoryChecked(categoryKey)" @change="toggleCategory(categoryKey)" />
              <label class="form-check-label font-weight-bold small" :for="`toggle-${categoryKey}`">
                Select All
              </label>
            </div>
          </div>
          <div v-for="column in categoryColumns" :key="column.key" class="form-check">
            <input class="form-check-input" type="checkbox" :id="column.key"
              :checked="findColumnInDisplay(column.key).visible" @change="toggleColumnVisibility(column.key)" />
            <label class="form-check-label" :for="column.key">
              {{ column.label }}
            </label>
          </div>
        </div>
      </div>
    </b-modal>
    <!-- ./ show/hide column modal -->

    <div class="table-responsive datalog-scroll-area">
      <div style="max-height: 70vh; height: 100%; overflow-y: auto;">
        <table class="table table-striped table-sm table-hover table-bordered text-center">
          <thead class="thead-light">
            <tr>
              <th v-for="(col, index) in visibleColumns" :key="col.key"
                :style="{ minWidth: col.minWidth * 1.5 + 'px' }" draggable="true" @dragstart="onDragStart(index)"
                @dragover.prevent @drop="onDrop(index)" @dragend="onDragEnd" class="draggable-column py-2 px-2">
                {{ col.label }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="data in pageRows" :key="data.timestamp" :class="data.rowClass"
              @mouseenter="$emit('row-hover', Number(data.timestamp) * 1000)"
              @mouseleave="$emit('row-hover', null)">
              <td v-for="(col, index) in visibleColumns" :key="col.key"
                :class="index === 0 ? 'sticky-column' : ''">
                {{ data[col.key] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Navigasi halaman (paginate per time-window) -->
    <div class="d-flex align-items-center justify-content-between mt-3 mb-3 px-1" v-if="page.totalPages > 0">
      <span class="text-muted small">{{ pageRangeLabel }}</span>
      <div class="d-flex align-items-center">
        <button class="btn btn-sm btn-outline-mantraz" :disabled="page.safePage <= 0" @click="prevPage">
          Prev
        </button>
        <span class="mx-3 small font-weight-bold">Page {{ page.safePage + 1 }} of {{ page.totalPages }}</span>
        <button class="btn btn-sm btn-outline-mantraz" :disabled="page.safePage >= page.totalPages - 1"
          @click="nextPage">
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable object-curly-newline */
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { sortPositions, buildDatalogColumns, buildDatalogRows } from '@/util/datalogModel';
import { getRpmTypeLabel } from '@/util/ship/fms-summary';
import { prettyLabel } from '@/util/ship/vesselDetail';
import { numericCell } from '@/util/number';
import paginate from '@/util/paginate';

export default {
  name: 'DatalogTable',
  props: {
    asset: {
      type: Object,
      default: null,
    },
    series: {
      type: Array,
      default: () => [],
    },
    interval: {
      type: String,
      default: 'hour',
    },
    unit: {
      type: Object,
      default: () => ({ speed: 'knot', distance: 'nm' }),
    },
    // Halaman yang gabungin sama isSuperAdmin sebelum kirim ke sini.
    highlightAnomaly: {
      type: Boolean,
      default: false,
    },
    exportFileName: {
      type: String,
      default: 'datalog',
    },
  },
  data() {
    return {
      displayColumns: [],
      defaultColumns: {},
      columns: {},
      isUpdatingColumns: false, // Flag to prevent saving during column update
      currentPage: 0, // Halaman aktif datalog (paginate per time-window)
    };
  },

  computed: {
    deviceStatus() {
      return this.asset?.deviceStatus;
    },
    flowmeterGroups() {
      return this.deviceStatus?.flowmeterGroups || [];
    },
    sortedRpmTypes() {
      return sortPositions(this.deviceStatus?.rpmTypes || []);
    },
    exhaustPositions() {
      const details = this.asset?.summary?.temperature?.summary?.tempDetails?.EXHAUST_TEMPERATURE || {};
      return sortPositions(Object.keys(details));
    },
    tankPositions() {
      const details = this.asset?.summary?.temperature?.summary?.tempDetails?.TEMPERATURE || {};
      return sortPositions(Object.keys(details));
    },
    // Dua gerbang, persis kayak sumber: flag hasPowermeter DAN data device-nya.
    // Tanpa flag-nya, kapal yang punya data tapi flag-nya mati bakal dapat
    // kolom powermeter yang dulu gak pernah muncul.
    // JANGAN di-sort: sumber bangun kolom dari Object.keys apa adanya. Yang
    // nyortir cuma getDeviceStructureKey, dan itu khusus bikin hash.
    powermeterDevices() {
      if (!this.deviceStatus?.hasPowermeter) return [];
      const devices = this.asset?.summary?.powermeter?.summary?.devices;
      return devices ? Object.keys(devices) : [];
    },
    // Satu config buat kolom & baris; kalau dipisah dua-duanya bisa geser sendiri.
    rowConfig() {
      return {
        interval: this.interval,
        rpmTypes: this.sortedRpmTypes,
        flowmeterGroups: this.flowmeterGroups,
        aeCount: this.deviceStatus?.aeCount || 0,
        exhaustPositions: this.exhaustPositions,
        tankPositions: this.tankPositions,
        powermeterDevices: this.powermeterDevices,
        rpmTypeLabel: getRpmTypeLabel,
        flowmeterLabel: prettyLabel,
        unit: this.unit,
        highlightAnomaly: this.highlightAnomaly,
      };
    },
    // Kolom cuma bergantung ke identitas aset + kapabilitas perangkat + interval.
    // Jangan pantau objek asset-nya: store memutasi trail/status/visibility in place,
    // dan di Map Viewer trail ditulis tiap tick.
    capabilitySignature() {
      const ds = this.deviceStatus || {};
      return JSON.stringify([
        this.asset?.massId,
        this.interval,
        ds.rpmTypes,
        ds.aeCount,
        ds.hasPowermeter,
        ds.hasExhaustTemperature,
        (ds.flowmeterGroups || []).map((g) => [g.name, g.hasIn, g.hasOut]),
        this.exhaustPositions,
        this.tankPositions,
        this.powermeterDevices,
      ]);
    },
    // b-modal butuh id unik; kalau dua instans hidup bareng, id kembar bikin
    // tombol satu halaman buka modal halaman lain.
    modalId() {
      // eslint-disable-next-line no-underscore-dangle
      return `datalog-columns-${this._uid}`;
    },
    rowsPerPage() {
      // Ukuran halaman per time-window: minute=3 jam, hour=1 hari, day=1 bulan, week=1 tahun.
      const map = { minute: 180, hour: 24, day: 31, week: 52 };
      return map[this.interval] || 180;
    },
    page() {
      // Satu sumber hitungan paging (start/end/totalPages/safePage), udah di-clamp.
      return paginate(this.series.length, this.rowsPerPage, this.currentPage);
    },
    pagedSeries() {
      return this.series.slice(this.page.start, this.page.end);
    },
    pageRows() {
      // Baris yang di-render: cuma halaman aktif, biar build berat nggak jalan utk semua data.
      return buildDatalogRows(this.pagedSeries, this.rowConfig);
    },
    // Semua baris, lepas dari paginasi. Dipakai export & pembanding regresi.
    exportRows() {
      return buildDatalogRows(this.series, this.rowConfig);
    },
    pageRangeLabel() {
      const rows = this.pageRows;
      if (!rows.length) return '';
      return `${rows[0].dateTime} → ${rows[rows.length - 1].dateTime}`;
    },
    columnsForModal() {
      // eslint-disable-next-line no-unused-vars
      const { dateTime, ...rest } = this.defaultColumns;
      return rest;
    },
    visibleColumns() {
      return this.displayColumns
        .filter((col) => col.visible)
        .map((col) => {
          // Satuan Speed & Cruise mengikuti dropdown unit yang dipilih user
          if (col.key === 'speed') {
            return { ...col, label: `Speed (${this.unit.speed})` };
          }
          if (col.key === 'cruise') {
            return { ...col, label: `Cruise (${this.unit.distance})` };
          }
          return col;
        });
    },
  },

  watch: {
    capabilitySignature: {
      handler() {
        this.rebuildColumns();
        this.currentPage = 0;
        this.$nextTick(() => { this.loadColumnsSuppressed(); });
      },
      immediate: true,
    },
    displayColumns: {
      handler(newColumns) {
        // Only save if not currently updating columns
        if (!this.isUpdatingColumns) {
          this.saveColumns(newColumns);
        }
      },
      deep: true,
    },
    series() {
      this.currentPage = 0;
    },
    // Baris ganti (halaman/sort/data) tanpa mouseleave -> kursor peta nyangkut.
    pageRows() {
      this.$emit('row-hover', null);
    },
  },

  beforeDestroy() {
    this.$emit('row-hover', null);
  },

  methods: {
    prevPage() {
      if (this.page.safePage > 0) this.currentPage = this.page.safePage - 1;
    },
    nextPage() {
      if (this.page.safePage < this.page.totalPages - 1) this.currentPage = this.page.safePage + 1;
    },

    rebuildColumns() {
      // Set flag to prevent watcher from saving during update
      this.isUpdatingColumns = true;
      const flat = buildDatalogColumns(this.rowConfig);
      // defaultColumns tetap bergrup: modal Filter Column & migrateAndSetColumns baca bentuk ini.
      const grouped = {};
      flat.forEach((col) => {
        if (!grouped[col.group]) grouped[col.group] = [];
        grouped[col.group].push(col);
      });
      this.defaultColumns = JSON.parse(JSON.stringify(grouped));
      this.columns = JSON.parse(JSON.stringify(grouped));
      this.displayColumns = Object.values(this.defaultColumns).flat();
      this.$nextTick(() => { this.isUpdatingColumns = false; });
    },

    categoryLabel(key) {
      // Heading kategori di modal show/hide. fm_* -> "Flowmeter <pretty>".
      if (key.startsWith('fm_')) return `Flowmeter ${prettyLabel(key.slice(3).toUpperCase())}`;
      const map = {
        gps: 'GPS',
        wave: 'Wave & Marine',
        ae: 'AE',
        powermeter: 'Powermeter',
        exhaust: 'Exhaust Temp',
        reservoir: 'Reservoir Temp',
      };
      if (map[key]) return map[key];
      return key.charAt(0).toUpperCase() + key.slice(1);
    },

    async downloadExcel() {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Data');

      // Use visibleColumns computed property to ensure export matches current filter
      const visibleColumns = this.visibleColumns;

      // Set header Excel berdasarkan kolom yang visible dan terurut
      worksheet.columns = visibleColumns.map((col) => ({
        header: col.label,
        key: col.key,
        width: 25, // Lebar kolom bisa disesuaikan
      }));

      // Export tetap SEMUA data (semua halaman), bukan cuma halaman aktif.
      this.exportRows.forEach((row) => {
        const rowData = {};
        visibleColumns.forEach((col) => {
          // Balikin ke Number biar sel Excel bisa di-SUM; teks kayak koordinat
          // & durasi dibiarin sama numericCell.
          rowData[col.key] = numericCell(row[col.key] ?? '-');
        });
        worksheet.addRow(rowData);
      });

      // Menyimpan file Excel
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `${this.exportFileName}.xlsx`);
    },

    showColumnModal() {
      this.$bvModal.show(this.modalId);
    },

    onDragStart(index) {
      this.dragSrcIndex = index;
    },

    onDrop(targetIndex) {
      if (this.dragSrcIndex === null || this.dragSrcIndex === targetIndex) {
        this.dragSrcIndex = null;
        return;
      }
      if (this.dragSrcIndex === 0 || targetIndex === 0) {
        this.dragSrcIndex = null;
        return;
      }

      // Langsung manipulasi 'displayColumns'
      const draggedCol = this.displayColumns.splice(this.dragSrcIndex, 1)[0];
      this.displayColumns.splice(targetIndex, 0, draggedCol);

      this.dragSrcIndex = null;
    },

    onDragEnd() {
      this.dragSrcIndex = null;
    },

    openIndexedDB() {
      return new Promise((resolve, reject) => {
        const request = indexedDB.open('mantraz');

        request.onupgradeneeded = function handleUpgrade(event) {
          const db = event.target.result;
          if (!db.objectStoreNames.contains('columns')) {
            db.createObjectStore('columns', { keyPath: 'id' });
          }
        };

        request.onsuccess = function handleSuccess(event) {
          resolve(event.target.result);
        };

        request.onerror = function handleError(event) {
          const message = `IndexedDB error: ${event?.target?.error?.message || event?.target?.errorCode || 'Unknown error'}`;
          reject(new Error(message));
        };
      });
    },

    getDeviceStructureKey() {
      // Generate storage key based on device structure/configuration
      const {
        aeCount = 0,
        hasExhaustTemperature = false,
        flowmeterGroups = [],
        rpmTypes = [],
        hasPowermeter = false,
      } = this.deviceStatus || {};

      // Sort rpmTypes to ensure consistent key generation
      const sortedRpmTypes = [...rpmTypes].sort().join(',');

      // Get powermeter device keys if exists
      let pmDevicesKey = '';
      if (hasPowermeter && this.asset?.summary?.powermeter?.summary?.devices) {
        const pmDevices = Object.keys(this.asset.summary.powermeter.summary.devices).sort();
        pmDevicesKey = pmDevices.join(',');
      }

      // Create hash-like key from device structure using simple string concatenation
      const fmGroupsKey = flowmeterGroups
        .map((g) => `${g.name}:${g.hasIn ? 1 : 0}${g.hasOut ? 1 : 0}`)
        .sort()
        .join(',');
      // Kolom GPS beda per interval (minute = Coordinate, selain itu First/Last Coordinate),
      // jadi layout-nya disimpan terpisah biar kolomnya nggak nyasar ke belakang.
      const gpsVariant = this.interval === 'minute' ? 'min' : 'agg';
      const structureKey = `${aeCount}|${hasExhaustTemperature}|${fmGroupsKey}|${sortedRpmTypes}|${hasPowermeter}|${pmDevicesKey}|${gpsVariant}`;

      // Simple hash function without bitwise operations
      let hash = 0;
      for (let i = 0; i < structureKey.length; i += 1) {
        hash = ((hash * 31) + structureKey.charCodeAt(i)) % 2147483647;
      }

      const key = `mantraz_datalog_struct_${Math.abs(hash).toString(36)}`;
      return key;
    },

    saveColumns(columns) {
      const storageKey = this.getDeviceStructureKey();

      // Save to localStorage as primary storage (simpler and more reliable for this use case)
      try {
        localStorage.setItem(storageKey, JSON.stringify(columns));
      } catch (err) {
        console.error('Failed to save columns to localStorage:', err.message);
      }

      // Also save to IndexedDB as backup (legacy support)
      this.openIndexedDB().then((db) => {
        const tx = db.transaction('columns', 'readwrite');
        const store = tx.objectStore('columns');
        // Simpan dengan key berdasarkan device structure
        store.put({ id: storageKey, columns });
      }).catch((err) => {
        console.error('Failed to open IndexedDB:', err.message);
      });
    },

    loadColumnsFromIndexedDB() {
      const storageKey = this.getDeviceStructureKey();
      return this.openIndexedDB().then((db) => new Promise((resolve, reject) => {
        const tx = db.transaction('columns', 'readonly');
        const store = tx.objectStore('columns');
        const request = store.get(storageKey);

        request.onsuccess = function handleGetSuccess(event) {
          resolve(event.target.result?.columns || null);
        };

        request.onerror = function handleGetError(evt) {
          const message = evt?.target?.error?.message || 'Failed to load columns';
          reject(new Error(message));
        };
      })).catch((err) => {
        console.error('Failed to open IndexedDB:', err.message);
      });
    },

    loadColumnsFromLocalStorage() {
      const storageKey = this.getDeviceStructureKey();
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          return JSON.parse(stored);
        }
        return null;
      } catch (err) {
        console.error('Failed to load columns from localStorage:', err.message);
        return null;
      }
    },

    resetColumns() {
      // Reset 'displayColumns' dengan meratakannya dari 'defaultColumns'
      this.displayColumns = Object.values(this.defaultColumns).flat();
    },

    loadColumnsForCurrentAsset() {
      // Priority: load from localStorage first (faster and simpler)
      const localStorageColumns = this.loadColumnsFromLocalStorage();
      if (localStorageColumns) {
        this.migrateAndSetColumns(localStorageColumns);
        return;
      }

      // Fallback to IndexedDB if localStorage is empty
      this.loadColumnsFromIndexedDB()
        .then((storedColumns) => {
          this.migrateAndSetColumns(storedColumns);
          // Migrate to localStorage for future use
          if (storedColumns) {
            this.saveColumns(storedColumns);
          }
        })
        .catch((err) => {
          console.warn('Could not load columns from IndexedDB:', err.message);
          this.resetColumns();
        });
    },

    loadColumnsSuppressed() {
      // Load layout tersimpan TANPA men-trigger auto-save. Penting karena kolom temp
      // (exhaust/reservoir) data-driven: bisa muncul/ilang tergantung data di range
      // terpilih. Tanpa guard ini, reconcile saat load bisa nimpa layout tersimpan user
      // (kolom yang lagi nggak ada datanya bisa kehapus permanen dari layout).
      this.isUpdatingColumns = true;
      this.loadColumnsForCurrentAsset();
      this.$nextTick(() => {
        this.isUpdatingColumns = false;
      });
    },

    /**
     * Menampilkan semua kolom.
     */
    showAllColumns() {
      // Tabel baca displayColumns, BUKAN this.columns: dua-duanya salinan
      // terpisah, jadi nulis ke this.columns gak nyampe ke layar.
      this.displayColumns = this.displayColumns.map((col) => ({ ...col, visible: true }));
    },

    /**
     * Menyembunyikan semua kolom, kecuali kolom 'dateTime' yang krusial.
     */
    hideAllColumns() {
      // dateTime dipertahankan: dia gak muncul di modal, jadi kalau ikut
      // disembunyiin user gak punya cara balikin.
      this.displayColumns = this.displayColumns.map((col) => (
        col.key === 'dateTime' ? col : { ...col, visible: false }
      ));
    },

    /**
     * Memeriksa apakah semua kolom dalam satu kategori sedang ditampilkan.
     * @param {string} categoryKey - Kunci kategori (misal: 'gps', 'port').
     * @returns {boolean} - True jika semua kolom visible.
     */
    isCategoryChecked(categoryKey) {
      const categoryColumns = this.defaultColumns[categoryKey];
      if (!categoryColumns) return false;
      const categoryKeys = categoryColumns.map((c) => c.key);
      return this.displayColumns
        .filter((col) => categoryKeys.includes(col.key))
        .every((col) => col.visible);
    },

    /**
     * Menampilkan atau menyembunyikan semua kolom dalam satu kategori.
     * Jika ada satu saja yang tersembunyi, maka akan menampilkan semua.
     * Jika semua sudah tampil, maka akan menyembunyikan semua.
     * @param {string} categoryKey - Kunci kategori yang akan di-toggle.
     */
    toggleCategory(categoryKey) {
      const categoryColumns = this.defaultColumns[categoryKey];
      if (!categoryColumns) return;

      const categoryColumnKeys = categoryColumns.map((c) => c.key);
      const shouldShow = this.displayColumns
        .filter((c) => categoryColumnKeys.includes(c.key))
        .some((c) => !c.visible);
      this.displayColumns = this.displayColumns.map((col) => {
        if (categoryColumnKeys.includes(col.key)) {
          return { ...col, visible: shouldShow };
        }
        return col;
      });
    },

    /**
     * Memigrasikan struktur kolom lama (array) ke struktur baru (objek)
     * dan mengatur data kolom komponen.
     * @param {Array|Object} loadedColumns - Kolom yang dimuat dari IndexedDB.
     */
    migrateAndSetColumns(loadedColumns) {
      if (!loadedColumns) {
        // Jika tidak ada data tersimpan, gunakan default
        this.resetColumns();
        return;
      }

      // Jika data tersimpan adalah array (struktur baru):
      // pertahankan urutan & visibility dari yang tersimpan, tapi SELALU ambil
      // label & minWidth terbaru dari kode (biar satuan kolom ikut update).
      if (Array.isArray(loadedColumns)) {
        const freshColumns = Object.values(this.defaultColumns).flat();
        const freshMap = new Map(freshColumns.map((col) => [col.key, col]));
        const savedKeys = new Set(loadedColumns.map((col) => col.key));

        const merged = loadedColumns
          .filter((col) => freshMap.has(col.key)) // buang kolom yang sudah tidak ada
          .map((col) => ({ ...freshMap.get(col.key), visible: col.visible }));

        // Tambahkan kolom baru yang belum ada saat setting disimpan
        freshColumns.forEach((col) => {
          if (!savedKeys.has(col.key)) {
            merged.push(col);
          }
        });

        this.displayColumns = merged;
      } else {
        // Jika data tersimpan adalah objek (struktur lama), lakukan migrasi
        console.warn('Old column structure detected. Migrating...');
        const oldVisibilityMap = new Map(Object.values(loadedColumns).flat().map((col) => [col.key, col.visible]));
        const newColumns = JSON.parse(JSON.stringify(Object.values(this.defaultColumns).flat()));
        newColumns.forEach((col) => {
          if (oldVisibilityMap.has(col.key)) {
            // eslint-disable-next-line no-param-reassign
            col.visible = oldVisibilityMap.get(col.key);
          }
        });
        this.displayColumns = newColumns;
      }
    },

    findColumnInDisplay(key) {
      return this.displayColumns.find((c) => c.key === key);
    },

    toggleColumnVisibility(key) {
      const index = this.displayColumns.findIndex((c) => c.key === key);
      if (index !== -1) {
        const newColumns = [...this.displayColumns];
        newColumns[index] = { ...newColumns[index], visible: !newColumns[index].visible };
        this.displayColumns = newColumns;
      }
    },
  },
};
</script>

<style scoped>
/* Biar tabel bisa nyusut ngikutin induk (mis. bottom sheet), bukan cuma 70vh tetap */
.datalog-table-root {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.datalog-scroll-area {
  flex: 1 1 auto;
  min-height: 0;
}

.sticky-column {
  position: sticky;
  left: 0;
}

.sticky-column:nth-child(odd) {
  background-color: var(--mz-neutral-light-bg) !important;
}

.sticky-column:nth-child(even) {
  background-color: var(--mz-neutral-light-bg) !important;
}

.draggable-column {
  cursor: move;
  user-select: none;
}

.draggable-column.dragging {
  opacity: 0.5;
}

.table {
  border-collapse: collapse;
}

th {
  position: sticky !important;
  top: 0 !important;
  border: 1px solid !important;
  padding: 8px !important;
  text-align: center !important;
  vertical-align: middle !important;
  font-size: var(--mz-fs-base) !important;
  background-color: var(--mz-table-head-bg) !important;
  color: var(--mz-table-head-fg) !important;
}

th:first-child {
  position: sticky !important;
  left: 0 !important;
  z-index: 5;
}

/* Highlight baris anomali (override striped + sticky column) */
.table tbody tr.anomaly-rpm-no-flow > td {
  background-color: #f8d7da !important; /* RPM > 0 tapi FLOW = 0 (merah muda) */
}

.table tbody tr.anomaly-flow-no-rpm > td {
  background-color: #fff3cd !important; /* RPM = 0 tapi FLOW > 0 (kuning) */
}
</style>
